import { createContext, useContext, useId, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import * as RadixAccordion from "@radix-ui/react-accordion";

import { IconFrame } from "@/ui-kit";

import { Typography } from "ui-kit/Typography";

import styles from "./Accordion.module.css";
import classNames from "classnames";
import Colors from "ui-kit/colors";
import { useElementHeight } from "hooks/useElementHeight";
import { ChevronDown, ChevronUp } from "../../icons";
import { TypographyVariant } from "ui-kit/Typography/types";

// Radix keeps which items are open to itself, and the chevron and the panel's
// height need to know. So `Accordion` holds the open values and each item
// reads its own.
const OpenItemsContext = createContext<string[]>([]);
const ItemExpandedContext = createContext(false);

export const AccordionPanel = ({
  children,
  hasIcon = false,
  className,
}: {
  children: ReactElement | string | null;
  hasIcon?: boolean;
  className?: string;
}) => {
  const isExpanded = useContext(ItemExpandedContext);
  const { ref, height } = useElementHeight();

  // The shell animating max-height is ours, and Radix's content sits inside
  // it: Radix zeroes transition-duration on its own node each time it opens or
  // closes, which would cut the transition short. `forceMount` keeps a closed
  // panel's text in the page, as before.
  return (
    <div
      className={classNames(
        styles.accordionPanel,
        {
          [styles.hasIcon]: hasIcon,
        },
        className
      )}
      aria-hidden={!isExpanded}
      style={{ maxHeight: isExpanded ? height : 0 }}
    >
      <RadixAccordion.Content
        forceMount
        // React 18 drops a boolean `inert`, so the empty string sets the
        // attribute; the cast hides it from the React 19-era `inert?: boolean`.
        {...({ inert: !isExpanded ? "" : undefined } as {})}
        ref={ref}
        className={classNames(
          styles.contentWrapper,
          typeof children === "string" && styles.contentWrapperString
        )}
      >
        {typeof children === "string" ? (
          <div className={styles.textContainer}>{children}</div>
        ) : (
          children
        )}
      </RadixAccordion.Content>
    </div>
  );
};

export const AccordionTitle = ({
  children,
  icon,
  className,
  titleStyle,
  iconStyle = "normal",
}: {
  children: string | ReactElement | ReactElement[] | null;
  /** A glyph from `ui-kit/icons`. Framed when `iconStyle` is set. */
  icon?: ReactElement;
  className?: string;
  titleStyle?: TypographyVariant;
  iconStyle?: "normal" | "round";
}) => {
  const isExpanded = useContext(ItemExpandedContext);

  return (
    <RadixAccordion.Trigger
      className={classNames(
        styles.accordionTitle,
        className,
        iconStyle === "round" && styles.iconRound,
        {
          [styles.isExpanded]: isExpanded,
        }
      )}
    >
      {icon && (
        <IconFrame
          icon={icon}
          className={styles.icon}
          iconColor={Colors.blue300}
          style={iconStyle}
        />
      )}
      {typeof children === "string" ? (
        <Typography
          className={styles.titleText}
          variant={titleStyle || "body2"}
        >
          <strong>{children}</strong>
        </Typography>
      ) : (
        <>{children}</>
      )}

      <IconFrame
        icon={isExpanded ? <ChevronUp /> : <ChevronDown />}
        iconSize={16}
        className={styles.iconChevron}
        iconColor={Colors.blue300}
      />
    </RadixAccordion.Trigger>
  );
};

export const AccordionItem = ({
  children,
  isHidden,
  className,
  value,
}: {
  children: ReactNode[] | ReactNode;
  isHidden?: boolean;
  className?: string;
  /** Names the item for `Accordion`'s `defaultValue`. Generated otherwise. */
  value?: string;
}) => {
  const generatedValue = useId();
  const itemValue = value ?? generatedValue;
  const isExpanded = useContext(OpenItemsContext).includes(itemValue);

  return (
    <RadixAccordion.Item
      value={itemValue}
      className={classNames(styles.accordionItem, className, {
        [styles.isHidden]: isHidden,
      })}
    >
      <ItemExpandedContext.Provider value={isExpanded}>
        {children}
      </ItemExpandedContext.Provider>
    </RadixAccordion.Item>
  );
};

export const Accordion = ({
  children,
  multiple,
  collapsible,
  className,
  defaultValue,
}: {
  children: ReactNode | ReactNode[];
  multiple?: boolean;
  collapsible?: boolean;
  className?: string;
  /** The `value` of the item that starts open. */
  defaultValue?: string;
}) => {
  const [openItems, setOpenItems] = useState<string[]>(
    defaultValue ? [defaultValue] : []
  );
  const rootClassName = classNames(styles.accordion, className);

  return (
    <OpenItemsContext.Provider value={openItems}>
      {multiple ? (
        <RadixAccordion.Root
          type="multiple"
          value={openItems}
          onValueChange={setOpenItems}
          className={rootClassName}
        >
          {children}
        </RadixAccordion.Root>
      ) : (
        <RadixAccordion.Root
          type="single"
          collapsible={collapsible}
          value={openItems[0] ?? ""}
          onValueChange={(value) => setOpenItems(value ? [value] : [])}
          className={rootClassName}
        >
          {children}
        </RadixAccordion.Root>
      )}
    </OpenItemsContext.Provider>
  );
};
